import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { WhyGrid } from "@/components/home/WhySection";
import FinalCTA from "@/components/home/FinalCTA";
import { IMAGES } from "@/data/content";

const CHAPTERS = [
  { no: "01", title: "Our Mission", body: "Help organizations turn complex, fragmented data into clear, intelligent decisions — and the automation and outcomes that follow." },
  { no: "02", title: "Our Vision", body: "A world where every business decision is supported by reliable data, sound analytics and practical AI — not guesswork." },
  { no: "03", title: "Our Approach", body: "We start with the business problem, design the right architecture, build production-grade systems, and keep improving them over time." },
  { no: "04", title: "Our Capabilities", body: "Data engineering, analytics and business intelligence, machine learning and AI, cloud data platforms and intelligent automation." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About | All Data Tech"
        description="All Data Tech is a technology company focused on data, analytics, AI, cloud and automation — because better data creates better decisions."
        path="/about"
      />
      <PageHero
        eyebrow="About"
        title={<>Better data creates <span className="text-gradient">better decisions.</span></>}
        sub="All Data Tech is a data and AI technology company. We help businesses harness the power of data to drive smarter decisions and measurable results."
      />

      <section className="ad-section bg-white">
        <div className="ad-container grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div>
              <span className="ad-eyebrow">Our story</span>
              <h2 className="mt-4 text-2xl font-extrabold text-[#111827] sm:text-3xl lg:text-4xl">A technology partner focused on outcomes.</h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-[#4B5563]">
                <p>All Data Tech specializes in harnessing the power of data to drive smarter decisions and help businesses thrive in a data-driven world.</p>
                <p>We provide data analytics, business intelligence, predictive analytics, custom data solutions, AI and machine learning, and cloud-based data services — designed to streamline operations, enhance decision-making and deliver actionable insights.</p>
                <p>Our focus is simple: build the technology layer that turns data complexity into a durable competitive advantage.</p>
              </div>
            </div>
          </Reveal>
          <Reveal index={1}>
            <div className="relative overflow-hidden rounded-[1.5rem] border border-[#111827]/8 shadow-[0_40px_80px_-45px_rgba(17,24,39,0.4)]">
              <div className="img-overlay-blue relative">
                <img src={IMAGES.aboutTeam} alt="All Data Tech team collaborating" className="h-80 w-full object-cover lg:h-[460px]" loading="lazy" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="ad-section bg-[#F7F8FA]">
        <div className="ad-container grid gap-6 md:grid-cols-2">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.no} index={i % 2}>
              <div className="flex gap-6 rounded-2xl border border-[#111827]/8 bg-white p-8">
                <span className="text-3xl font-extrabold text-[#1677FF]/25">{c.no}</span>
                <div>
                  <h3 className="text-lg font-bold text-[#111827]">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4B5563]">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="ad-section bg-white">
        <div className="ad-container">
          <div className="max-w-2xl">
            <span className="ad-eyebrow">What sets us apart</span>
            <h2 className="mt-4 text-2xl font-extrabold text-[#111827] sm:text-3xl lg:text-4xl">Built around <span className="text-gradient">your outcomes.</span></h2>
          </div>
          <div className="mt-12"><WhyGrid /></div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
